import { useIonModal } from '@ionic/react'

import { QUERY_KEY_GET_PINNED_UNITS_LIST } from '@apps-common/apis/portal/dash/get-dash-pinned-list'
import { useGetDashPinnedUnit } from '@apps-common/apis/portal/dash/get-dash-pinned-unit'
import { usePinDeviceToDashboard } from '@apps-common/apis/portal/dash/pin-device-to-dashboard'
import { useUnPinDeviceFromDashboard } from '@apps-common/apis/portal/dash/un-pin-device-from-dashboard'
import { PinUnitModal } from '@apps-common/apps/portal/pages/units/pages/manage/components/pin-unit/PinUnitModal'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { unitIsCancelled, unitIsInactive } from '@apps-common/helpers/unit-helpers'
import { queryClient } from '@apps-common/lib'
import { defaultModalAnimations } from '@apps-common/lib/modal-animations'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'

interface IProps {
  loading?: boolean
  unit: IUnitGenericDetailsModel | undefined
}

export const PinUnitButton: React.FC<IProps> = ({ loading, unit }) => {
  const {
    data: pinnedUnit,
    isLoading: pinnedUnitIsLoading,
    isRefetching: pinnedUnitIsRefetching,
    refetch: pinnedUnitRefetch
  } = useGetDashPinnedUnit({ config: { staleTime: 0, retry: 0, enabled: !!unit }, unitId: unit?.unitId || '' })

  const { mutate: pinDevice, isPending: isPendingDevicePin } = usePinDeviceToDashboard(true, (data) => {
    pinnedUnitRefetch()
    if (data) {
      queryClient.refetchQueries({ queryKey: QUERY_KEY_GET_PINNED_UNITS_LIST() })
    }
  })
  const { mutate: unPinDevice, isPending: isPendingDeviceUnPin } = useUnPinDeviceFromDashboard(true, (data) => {
    pinnedUnitRefetch()
    if (data) {
      queryClient.refetchQueries({ queryKey: QUERY_KEY_GET_PINNED_UNITS_LIST() })
    }
  })

  const isLoading = loading || pinnedUnitIsLoading
  const updating = pinnedUnitIsRefetching || isPendingDevicePin || isPendingDeviceUnPin

  const id = (pinnedUnit?.data as any)?.id || 0

  const [presentPinUnitModal, dismissPinUnitModal] = useIonModal(PinUnitModal, {
    componentProps: {},
    onDismiss: (data: 'Account' | 'Company', role: string) => {
      if (role === ModalDismiss.Confirm && data) {
        pinDevice({ type: data, unitId: unit?.unitId || '' })
      }
      dismissPinUnitModal()
    }
  })

  const inActive = unitIsInactive(unit)
  const cancelled = unitIsCancelled(unit)

  return (
    <>
      {!pinnedUnit?.isPinned && (
        <ButtonRoundSm
          type='button'
          size='lg'
          loading={updating || inActive || cancelled}
          className='action-success'
          callback={() => {
            presentPinUnitModal({
              ...defaultModalAnimations
            })
          }}
          tooltip={{
            id: 'unit-pin-to-dashboard',
            message: 'Pin device to dashboard',
            place: 'bottom'
          }}
        >
          <commonIcons.pinUnit />
        </ButtonRoundSm>
      )}
      {pinnedUnit?.isPinned && (
        <ModalButton
          component={ConfirmModal}
          disabled={updating || inActive || cancelled}
          classes=''
          componentProps={{
            text: 'Un-pin device',
            description: 'Confirm you would like to un-pin this device? It will be removed from pinned devices on your dashboard.',
            action: ConfirmModalAction.Warning
          }}
          confirmCallback={() => unPinDevice({ id })}
          tooltip={{
            id: 'unit-un-pin-from-dashboard',
            message: 'Un-pin device from dashboard',
            place: 'bottom'
          }}
        >
          <ButtonRoundSm type='div' size='lg' className='action-warning'>
            <commonIcons.unPinUnit />
          </ButtonRoundSm>
        </ModalButton>
      )}
    </>
  )
}
